import type { GatewayBrowserClient } from "../gateway.ts";

export type FileEntry = {
  name: string;
  path: string;
  type: "directory" | "file" | "symlink";
  size: number | null;
  sizeFormatted: string | null;
  modifiedAt: number | null;
  isText: boolean | null;
};

export type FilesListResult = {
  path: string;
  parent: string;
  items: FileEntry[];
};

export type FileReadResult = {
  path: string;
  name: string;
  content: string;
  size: number;
  sizeFormatted: string;
  modifiedAt: number;
};

export type FilesState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  filesCurrentPath: string;
  filesLoading: boolean;
  filesError: string | null;
  filesList: FilesListResult | null;
  filesShowHidden: boolean;
  // File viewer/editor
  filesOpenFile: FileReadResult | null;
  filesOpenFileLoading: boolean;
  filesOpenFileError: string | null;
  filesEditDraft: string | null;
  filesEditSaving: boolean;
};

export async function loadDirectory(state: FilesState, dirPath?: string) {
  if (!state.client || !state.connected) return;
  if (state.filesLoading) return;

  const targetPath = dirPath ?? state.filesCurrentPath;
  state.filesLoading = true;
  state.filesError = null;
  state.filesOpenFile = null;
  state.filesEditDraft = null;

  try {
    const result = (await state.client.request("files.list", {
      path: targetPath,
      showHidden: state.filesShowHidden,
    })) as FilesListResult;
    state.filesList = result;
    state.filesCurrentPath = result.path;
  } catch (err) {
    state.filesError = String(err);
  } finally {
    state.filesLoading = false;
  }
}

export async function openFile(state: FilesState, filePath: string) {
  if (!state.client || !state.connected) return;

  state.filesOpenFileLoading = true;
  state.filesOpenFileError = null;
  state.filesEditDraft = null;

  try {
    const result = (await state.client.request("files.read", {
      path: filePath,
    })) as FileReadResult;
    state.filesOpenFile = result;
    state.filesEditDraft = result.content;
  } catch (err) {
    state.filesOpenFileError = String(err);
  } finally {
    state.filesOpenFileLoading = false;
  }
}

export async function saveFile(state: FilesState) {
  if (!state.client || !state.connected) return;
  if (!state.filesOpenFile || state.filesEditDraft === null) return;

  state.filesEditSaving = true;
  try {
    await state.client.request("files.write", {
      path: state.filesOpenFile.path,
      content: state.filesEditDraft,
    });
    // Refresh the file to get updated metadata
    const result = (await state.client.request("files.read", {
      path: state.filesOpenFile.path,
    })) as FileReadResult;
    state.filesOpenFile = result;
    state.filesEditDraft = result.content;
  } catch (err) {
    state.filesOpenFileError = String(err);
  } finally {
    state.filesEditSaving = false;
  }
}

export function closeFile(state: FilesState) {
  state.filesOpenFile = null;
  state.filesOpenFileError = null;
  state.filesEditDraft = null;
}

export async function deleteItem(state: FilesState, itemPath: string) {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request("files.delete", { path: itemPath });
    await loadDirectory(state);
  } catch (err) {
    state.filesError = String(err);
  }
}
